/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef } from "react";
import { PublicLayout } from "@/components/website/PublicLayout";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Phone, Mail, MapPin, Calendar, Users, ChefHat, CheckCircle, XCircle,
  MessageSquare, Loader2, CreditCard, Banknote, ArrowDown, Clock, CircleDollarSign
} from "lucide-react";

import { QuoteResponseButtons, QuoteTopActions } from "@/components/quotes/QuoteTopActions";
import { PaymentMethodTotal } from "@/components/quotes/PaymentMethodTotal";
import { PaymentAmountChoice, type PaymentAmount } from "@/components/quotes/PaymentAmountChoice";
import { usePublicPaymentOptions, hasDepositChoice } from "@/hooks/usePublicPaymentOptions";
import type { PublicChargeKind } from "../../supabase/functions/square-payments/charge.ts";
import { usePricingTypes, getPricingLabel, getUnitLabel } from "@/hooks/usePricingTypes";
import { getDocumentLabel, SERVICE_TYPE_LABELS, type ServiceType, getEventTimeLabel, formatEventStartTime } from "@/types/quotes";
import { recordCheckoutContext } from "@/lib/purchase-tracking";

interface CompanyInfo {
  company_name: string;
  company_phone: string;
  company_email: string;
  company_address: string;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
  sent: { label: "Awaiting Response", className: "bg-accent/15 text-accent-foreground border-accent/30" },
  accepted: { label: "Approved", className: "bg-primary/15 text-primary border-primary/30" },
  declined: { label: "Declined", className: "bg-destructive/15 text-destructive border-destructive/30" },
};

const PublicQuotePage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { data: pricingTypes } = usePricingTypes();
  const [quote, setQuote] = useState<any>(null);
  const [company, setCompany] = useState<CompanyInfo>({ company_name: "", company_phone: "", company_email: "", company_address: "" });
  const [paymentSettings, setPaymentSettings] = useState<{ etransfer_email: string; etransfer_instructions: string }>({ etransfer_email: "", etransfer_instructions: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revisionMessage, setRevisionMessage] = useState("");
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [responded, setResponded] = useState(false);

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"square" | "etransfer">("square");
  const [paymentAmount, setPaymentAmount] = useState<PaymentAmount>("deposit");
  const [processingPayment, setProcessingPayment] = useState(false);
  const [etransferDetails, setEtransferDetails] = useState<{ email: string; instructions: string; deposit_percent: number | null; deposit_amount: number; payment_type?: PublicChargeKind } | null>(null);

  // Deposit vs pay-in-full applies to the first payment only (#983).
  const firstPaymentPending =
    !!quote && Number(quote.amount_paid || 0) === 0 && quote.payment_status !== "paid" &&
    (quote.status === "sent" || quote.status === "accepted");
  const paymentOptions = usePublicPaymentOptions(quote?.id, firstPaymentPending);
  const amountChoice = firstPaymentPending && hasDepositChoice(paymentOptions) ? paymentOptions : null;
  const showAmountChoice = amountChoice !== null;
  const payInFull = showAmountChoice && paymentAmount === "full";
  // What the next click charges when the customer picks deposit or full (#1163).
  const chosenAmount = amountChoice ? (payInFull ? amountChoice.total_amount : amountChoice.deposit_amount) : null;

  const actionRef = useRef<HTMLDivElement>(null);
  const etransferCalloutRef = useRef<HTMLDivElement>(null);
  const squareCalloutRef = useRef<HTMLDivElement>(null);
  const prevPaymentStatusRef = useRef<string | null>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Intersection observer for sticky mobile CTA
  useEffect(() => {
    if (!actionRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(actionRef.current);
    return () => observer.disconnect();
    // `loading` is what mounts the action card: setQuote lands while the
    // spinner is still up, so without it the observer never attaches (#1131).
  }, [loading, quote, responded]);

  // When payment_status transitions (not on initial load), scroll the
  // matching callout into view so the customer's next step is obvious.
  // Uses rAF to wait for layout after the state flip that mounts the
  // callout, and skips if the element is already fully visible.
  useEffect(() => {
    const status = quote?.payment_status ?? null;
    const prev = prevPaymentStatusRef.current;
    prevPaymentStatusRef.current = status;
    if (prev === null || prev === status) return;

    const ref =
      status === "awaiting_etransfer" ? etransferCalloutRef :
      status === "link_sent" ? squareCalloutRef :
      null;
    if (!ref) return;

    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const fullyVisible = rect.top >= 0 && rect.bottom <= vh;
      if (fullyVisible) return;
      // Use "start" with offset on mobile to clear the sticky CTA bar; on
      // desktop "center" reads nicely. 768px matches the Tailwind md breakpoint.
      const block: ScrollLogicalPosition = window.innerWidth < 768 ? "start" : "center";
      el.scrollIntoView({ behavior: "smooth", block });
    });
  }, [quote?.payment_status]);

  useEffect(() => {
    async function load() {
      if (!id) return;
      setLoading(true);
      try {
        const { data: q, error: qe } = await supabase
          .rpc("get_public_quote", { _quote_id: id });
        if (qe) throw qe;
        if (!q) throw new Error("Quote not found");
        const quoteData = q as any;
        setQuote(quoteData);
        // #853: anon has no SELECT policy on quote_responses (by design, #762),
        // so "already responded" comes from the SECURITY DEFINER RPC as one
        // boolean: a response at or after the quote's last update.
        setResponded(quoteData.has_responded_since_update === true);

        const { data: settings } = await (supabase as any)
          .from("app_settings")
          .select("key, value")
          .in("key", ["company_name", "company_phone", "company_email", "company_address"]);
        if (settings) {
          const info: any = {};
          settings.forEach((s: any) => {
            if (s.key.startsWith("company_")) info[s.key] = s.value;
          });
          setCompany(info as CompanyInfo);
        }

        // The e-transfer address and instructions are no longer anon-readable
        // from app_settings (#681). They come from a quote-scoped RPC that only
        // answers for a quote already awaiting an e-transfer, so a customer who
        // reloads the link still sees where to send the money — that reload is
        // the only path where `etransferDetails` (set during the approve action)
        // is empty. Destructured without rethrowing, like the settings query
        // above: a failure here leaves the fields blank rather than erroring the
        // whole page out.
        const { data: etransfer } = await (supabase as any).rpc(
          "get_public_etransfer_details",
          { _quote_id: id }
        );
        if (etransfer) {
          setPaymentSettings({
            etransfer_email: etransfer.etransfer_email ?? "",
            etransfer_instructions: etransfer.etransfer_instructions ?? "",
          });
        }
      } catch (err: any) {
        setError(err.message || "Quote not found");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  useEffect(() => {
    if (!loading && quote && searchParams.get("print") === "true") {
      setTimeout(() => window.print(), 500);
    }
  }, [loading, quote, searchParams]);

  const handleApproveClick = () => {
    setPaymentDialogOpen(true);
  };

  const handlePaymentConfirm = async () => {
    if (!quote) return;
    setProcessingPayment(true);
    try {
      // Accept first, so a quote that's no longer respondable (#1049) fails
      // before a response row is written or a payment link is requested.
      const { error: rpcError } = await supabase.rpc("respond_to_quote" as any, {
        _quote_id: quote.id,
        _new_status: "accepted",
      });
      if (rpcError) throw rpcError;

      const { error: re } = await (supabase as any)
        .from("quote_responses")
        .insert({
          quote_id: quote.id,
          action: "approved",
          respondent_name: quote.client_name,
          respondent_email: quote.client_email,
        });
      if (re) throw re;

      const { data, error: fnError } = await supabase.functions.invoke("square-payments", {
        body: {
          action: "public-payment-link",
          quote_id: quote.id,
          method: paymentMethod,
          pay_in_full: payInFull,
        },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      recordCheckoutContext(quote.id, quote.total);

      if (paymentMethod === "square" && data?.payment_link) {
        setQuote((prev: any) => ({ ...prev, status: "accepted", square_payment_link: data.payment_link, payment_status: "link_sent" }));
        setResponded(true);
        setPaymentDialogOpen(false);
        toast.success("Quote approved! Redirecting to payment...");
        setTimeout(() => { window.open(data.payment_link, "_blank"); }, 1000);
      } else if (paymentMethod === "etransfer") {
        setQuote((prev: any) => ({ ...prev, status: "accepted", payment_status: "awaiting_etransfer" }));
        setEtransferDetails({
          email: data.etransfer_email,
          instructions: data.etransfer_instructions,
          deposit_percent: data.deposit_percent,
          deposit_amount: data.deposit_amount,
          payment_type: data.payment_type,
        });
        setResponded(true);
        setPaymentDialogOpen(false);
        toast.success("Quote approved! Check your e-transfer instructions.");
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePaymentOnly = async () => {
    if (!quote) return;
    setProcessingPayment(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("square-payments", {
        body: {
          action: "public-payment-link",
          quote_id: quote.id,
          method: paymentMethod,
          pay_in_full: payInFull,
        },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      recordCheckoutContext(quote.id, quote.total);

      if (paymentMethod === "square" && data?.payment_link) {
        setQuote((prev: any) => ({ ...prev, square_payment_link: data.payment_link, payment_status: "link_sent" }));
        toast.success("Redirecting to payment...");
        setTimeout(() => { window.open(data.payment_link, "_blank"); }, 1000);
      } else if (paymentMethod === "etransfer") {
        setQuote((prev: any) => ({ ...prev, payment_status: "awaiting_etransfer" }));
        setEtransferDetails({
          email: data.etransfer_email,
          instructions: data.etransfer_instructions,
          deposit_percent: data.deposit_percent,
          deposit_amount: data.deposit_amount,
          payment_type: data.payment_type,
        });
        toast.success("E-transfer instructions are ready.");
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleAction = async (action: "declined" | "revision_requested", message?: string) => {
    if (!quote) return;
    setSubmitting(true);
    try {
      // Decline first, so a quote that's no longer respondable (#1018) fails
      // before a response row is written or the page flips to declined.
      if (action === "declined") {
        const { error: rpcError } = await supabase.rpc("respond_to_quote" as any, {
          _quote_id: quote.id,
          _new_status: "declined",
        });
        if (rpcError) throw rpcError;
        setQuote((prev: any) => ({ ...prev, status: "declined" }));
      }

      const { error: re } = await (supabase as any)
        .from("quote_responses")
        .insert({
          quote_id: quote.id,
          action,
          message: message || null,
          respondent_name: quote.client_name,
          respondent_email: quote.client_email,
        });
      if (re) throw re;

      setResponded(true);
      setShowRevisionForm(false);
      toast.success(
        action === "declined" ? "Quote declined." : "Revision request sent!"
      );
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToAction = () => {
    actionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (loading) {
    return (
      <PublicLayout className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </PublicLayout>
    );
  }

  if (error || !quote) {
    return (
      <PublicLayout className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-12 text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h1 className="font-display text-xl font-semibold mb-2">Document Not Found</h1>
            <p className="text-muted-foreground text-sm">This document may have been removed or the link is invalid.</p>
          </CardContent>
        </Card>
      </PublicLayout>
    );
  }

  const isActionable = quote.status === "sent" && !responded;
  const etransferEmail = etransferDetails?.email || paymentSettings.etransfer_email;
  const etransferInstructions = etransferDetails?.instructions || paymentSettings.etransfer_instructions;
  const depositAmount = etransferDetails?.deposit_amount ?? Number(quote.total);
  const depositPercent = etransferDetails?.deposit_percent;

  const amountPaid = Number(quote.amount_paid || 0);
  const totalAmount = Number(quote.total || 0);
  const balanceRemaining = Math.max(0, totalAmount - amountPaid);
  const paymentProgress = totalAmount > 0 ? Math.min(100, (amountPaid / totalAmount) * 100) : 0;
  const surchargePercent = Number(quote.card_surcharge_percent) || 0;
  const methodTotal = (
    <PaymentMethodTotal
      method={paymentMethod}
      amount={chosenAmount ?? (amountPaid > 0 ? balanceRemaining : totalAmount)}
      isBalance={amountPaid > 0}
      isDeposit={showAmountChoice && !payInFull}
      surchargePercent={surchargePercent}
    />
  );

  const statusConfig = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft;
  const docLabel = getDocumentLabel(quote.status, quote.payment_status);

  // Only an accepted, unpaid quote gets the bottom bar. A sent quote's
  // Approve / Decline are already pinned at the top (#815/#856, #1131).
  const stickyCtaLabel =
    quote.status === "accepted" && quote.payment_status !== "paid" ? "Complete Payment" : null;

  // Does the action section need to render?
  const hasActionSection =
    isActionable ||
    (quote.status === "accepted" && quote.payment_status !== "paid") ||
    (quote.status === "accepted" && quote.payment_status === "paid") ||
    quote.status === "declined" ||
    (responded && quote.status === "sent");

  return (
    <PublicLayout className="min-h-screen bg-muted/30">
      {/* ─── Sticky Header (+ pinned Approve / Decline while actionable) ─── */}
      <div className="sticky top-0 z-50 shadow-lg print:static print:shadow-none">
        <header className="bg-foreground text-background">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <div>
              <h1 className="font-logo text-lg sm:text-xl font-bold leading-tight">
                {company.company_name || `Catering ${docLabel}`}
              </h1>
              <p className="text-xs font-secondary opacity-60 mt-0.5">
                {docLabel} {quote.quote_number}
              </p>
            </div>
            <Badge className={`${statusConfig.className} text-xs font-medium px-2.5 py-1 rounded-full`}>
              {statusConfig.label}
            </Badge>
          </div>
          <div className="h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent" />
        </header>
        {isActionable && (
          <QuoteTopActions
            docLabel={docLabel}
            submitting={submitting}
            onApprove={handleApproveClick}
            onDecline={() => handleAction("declined")}
          />
        )}
      </div>

      <main id="main-content" tabIndex={-1} className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6 focus:outline-none">
        {/* ─── Notes (compact, top of document) ─── */}
        {quote.notes?.trim() && (
          <section
            aria-labelledby="quote-notes-heading"
            className="rounded-lg border border-border bg-background px-4 py-3"
          >
            <h2
              id="quote-notes-heading"
              className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-1"
            >
              Notes
            </h2>
            <p className="text-sm text-muted-foreground whitespace-pre-line break-words">{quote.notes}</p>
          </section>
        )}

        {/* ─── 1. Client & Event Info ─── */}
        <div className="grid sm:grid-cols-2 gap-4">
          <Card className="shadow-sm">
            <CardContent className="pt-5 pb-5 space-y-2">
              <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                Prepared For
              </h2>
              <div className="font-medium text-sm">{quote.client_name}</div>
              {quote.client_email && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Mail className="h-3 w-3 shrink-0" /> {quote.client_email}
                </div>
              )}
              {quote.client_phone && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Phone className="h-3 w-3 shrink-0" /> {quote.client_phone}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="pt-5 pb-5 space-y-3">
              <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                Event Details
              </h2>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <Calendar className="h-4 w-4 shrink-0 text-primary" />
                  <span className="font-medium">
                    {quote.event_date
                      ? new Date(quote.event_date + "T00:00:00").toLocaleDateString("en-US", {
                          weekday: "short", month: "long", day: "numeric", year: "numeric",
                        })
                      : "TBD"}
                  </span>
                </div>
                {quote.event_start_time && (
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Clock className="h-4 w-4 shrink-0 text-primary" />
                    <span className="font-medium">
                      {getEventTimeLabel(quote.service_type)}: {formatEventStartTime(quote.event_start_time)}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <Users className="h-4 w-4 shrink-0 text-primary" />
                  <span className="font-medium">{quote.guest_count} guests</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <ChefHat className="h-4 w-4 shrink-0 text-primary" />
                  <span className="font-medium">{SERVICE_TYPE_LABELS[quote.service_type as ServiceType] ?? quote.service_type}</span>
                </div>
                {quote.event_address && (
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <MapPin className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                    <div className="space-y-1">
                      <span className="font-medium whitespace-pre-line block">{quote.event_address}</span>
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(quote.event_address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        View on map
                      </a>
                    </div>
                  </div>
                )}
                {quote.event_type?.name && (
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <MapPin className="h-4 w-4 shrink-0 text-primary" />
                    <span className="font-medium">{quote.event_type.name}</span>
                  </div>
                )}
                {quote.payment_due_date && (
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Clock className="h-4 w-4 shrink-0 text-primary" />
                    <span className="font-medium">
                      Payment due {new Date(quote.payment_due_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── 2. Itemized Quote Table ─── */}
        {quote.line_items?.length > 0 && (
          <Card className="shadow-sm overflow-hidden">
            <CardContent className="p-0">
              <div className="px-5 pt-5 pb-3">
                <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Itemized {docLabel}
                </h2>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider">Item</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-right">Price</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-right">Qty</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quote.line_items
                    .sort((a: any, b: any) => a.sort_order - b.sort_order)
                    .map((li: any) => (
                      <TableRow key={li.id}>
                        <TableCell className="py-3">
                          <div className="text-sm font-medium">{li.name}</div>
                          {li.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line">{li.description}</p>
                          )}
                          <Badge variant="outline" className="text-[10px] px-1 py-0 mt-1 font-normal">
                            {getPricingLabel(pricingTypes, li.pricing_type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-right align-top py-3">
                          ${Number(li.unit_price).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-sm text-right align-top py-3">
                          <div>{li.quantity}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {getUnitLabel(pricingTypes, li.pricing_type)}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-right font-medium align-top py-3">
                          ${Number(li.line_total).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>

              {/* Totals */}
              <div className="px-5 pb-5 pt-3">
                <div className="space-y-1.5 text-sm max-w-xs ml-auto">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${Number(quote.subtotal).toFixed(2)}</span>
                  </div>
                  {Number(quote.discount_amount) > 0 && (
                    <div className="flex justify-between text-destructive">
                      <span>Discount</span>
                      <span>−${Number(quote.discount_amount).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(quote.delivery_amount) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Delivery</span>
                      <span>${Number(quote.delivery_amount).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(quote.service_charge_amount) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Service charge</span>
                      <span>${Number(quote.service_charge_amount).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(quote.gratuity_amount) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Gratuity{Number(quote.gratuity_percent) > 0 ? ` (${Number(quote.gratuity_percent)}%)` : ""}
                      </span>
                      <span>${Number(quote.gratuity_amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax ({Number(quote.tax_rate)}%)</span>
                    <span>${Number(quote.tax_amount).toFixed(2)}</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between items-center font-bold text-lg pt-1 bg-primary/5 -mx-3 px-3 py-2 rounded-lg">
                    <span>Total</span>
                    <span className="text-primary text-xl">${totalAmount.toFixed(2)}</span>
                  </div>
                  {/* Show paid progress if any payments have been made */}
                  {amountPaid > 0 && (
                    <div className="pt-3 space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Amount Paid</span>
                        <span className="text-primary font-semibold">${amountPaid.toFixed(2)}</span>
                      </div>
                      <div className="relative">
                        <Progress value={paymentProgress} className="h-3" />
                        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-primary-foreground mix-blend-difference">
                          {Math.round(paymentProgress)}%
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Balance Remaining</span>
                        <span className="font-bold text-foreground">${balanceRemaining.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ─── 4. Action Section (context-aware) ─── */}
        <div ref={actionRef}>
          {hasActionSection && (
            <Card className="shadow-md border-t-4 border-t-primary no-print">
              <CardContent className="pt-6 pb-6 space-y-5">
                {/* ── Sent: Approve / Decline / Revision ── */}
                {isActionable && (
                  <>
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-accent" />
                      <h2 className="font-display text-base font-semibold">Your Response</h2>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Please review the quote above and let us know how you'd like to proceed.
                    </p>
                    <QuoteResponseButtons
                      docLabel={docLabel}
                      submitting={submitting}
                      onApprove={handleApproveClick}
                      onDecline={() => handleAction("declined")}
                    >
                      <Button
                        variant="ghost"
                        onClick={() => setShowRevisionForm(!showRevisionForm)}
                        disabled={submitting}
                        className="flex-1"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Request Revision
                      </Button>
                    </QuoteResponseButtons>
                    {showRevisionForm && (
                      <div className="space-y-3 pt-2">
                        <Textarea
                          placeholder="Tell us what changes you'd like..."
                          value={revisionMessage}
                          onChange={(e) => setRevisionMessage(e.target.value)}
                          rows={3}
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => setShowRevisionForm(false)}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleAction("revision_requested", revisionMessage)}
                            disabled={!revisionMessage.trim() || submitting}
                          >
                            {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                            Send Request
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ── Responded but still "sent" ── */}
                {responded && quote.status === "sent" && (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary shrink-0" />
                    <p className="text-sm">Your response has been recorded. Thank you!</p>
                  </div>
                )}

                {/* ── Accepted + NOT fully paid: unified payment section ── */}
                {quote.status === "accepted" && quote.payment_status !== "paid" && (
                  <>
                    {/* Payment progress bar for partial payments */}
                    {amountPaid > 0 && (
                      <div className="bg-primary/5 rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2 mb-1">
                          <CircleDollarSign className="h-5 w-5 text-primary" />
                          <h2 className="font-display text-base font-semibold">Payment Progress</h2>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Paid so far</span>
                          <span className="font-semibold text-primary">${amountPaid.toFixed(2)}</span>
                        </div>
                        <div className="relative">
                          <Progress value={paymentProgress} className="h-3" />
                          <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-primary-foreground mix-blend-difference">
                            {Math.round(paymentProgress)}%
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Remaining</span>
                          <span className="font-bold">${balanceRemaining.toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    {/* E-transfer instructions callout (if currently awaiting) */}
                    {quote.payment_status === "awaiting_etransfer" && etransferEmail && (
                      <div ref={etransferCalloutRef} className="border-l-4 border-primary bg-primary/5 rounded-r-lg p-4 space-y-2">
                        <div className="flex items-center gap-2">
                          <Banknote className="h-5 w-5 text-primary" />
                          <h3 className="font-display text-sm font-semibold">E-Transfer Instructions</h3>
                        </div>
                        <div className="text-sm space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Send to:</span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(etransferEmail);
                                toast.success("Email copied!");
                              }}
                              className="font-semibold text-primary hover:underline cursor-pointer flex items-center gap-1"
                            >
                              {etransferEmail}
                              <Mail className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">
                              {depositPercent
                                ? `Deposit (${depositPercent}%)`
                                : etransferDetails?.payment_type === "full" ? "Full Amount" : "Remaining Balance"}:
                            </span>
                            <span className="font-semibold">${depositAmount.toFixed(2)}</span>
                          </div>
                          {etransferInstructions && (
                            <p className="text-xs text-muted-foreground pt-2 border-t border-border">
                              {etransferInstructions}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Square link ready callout (if link was generated) */}
                    {quote.payment_status === "link_sent" && quote.square_payment_link && (
                      <div ref={squareCalloutRef} className="border-l-4 border-primary bg-primary/5 rounded-r-lg p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-5 w-5 text-primary" />
                          <h3 className="font-display text-sm font-semibold">Payment Link Ready</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Click below to complete your payment securely via Square.
                        </p>
                        <Button asChild className="w-full" size="lg">
                          <a href={quote.square_payment_link} target="_blank" rel="noopener noreferrer">
                            <CreditCard className="h-4 w-4 mr-2" />
                            Pay Now
                          </a>
                        </Button>
                      </div>
                    )}

                    <Separator />

                    {/* Always-visible payment picker */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <CircleDollarSign className="h-5 w-5 text-primary" />
                        <h2 className="font-display text-base font-semibold">
                          {amountPaid > 0 ? "Make Another Payment" : "Complete Your Payment"}
                        </h2>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {amountPaid > 0
                          ? "You can make additional payments toward your remaining balance."
                          : showAmountChoice
                            ? "Pay a deposit to confirm your booking, or pay the full amount now. Then choose how you'd like to pay."
                            : "A deposit is required to confirm your booking. Choose your preferred payment method below."}
                      </p>
                      {amountChoice && (
                        <PaymentAmountChoice
                          value={paymentAmount}
                          onChange={setPaymentAmount}
                          options={amountChoice}
                          idPrefix="inline"
                        />
                      )}
                      <RadioGroup
                        value={paymentMethod}
                        onValueChange={(v) => setPaymentMethod(v as "square" | "etransfer")}
                        className="space-y-3"
                      >
                        <Label
                          htmlFor="inline-method-square"
                          className="flex items-center gap-3 border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
                        >
                          <RadioGroupItem value="square" id="inline-method-square" />
                          <CreditCard className="h-5 w-5 text-muted-foreground shrink-0" />
                          <div>
                            <div className="font-medium text-sm">Credit Card</div>
                            <p className="text-xs text-muted-foreground">Pay securely online via Square</p>
                          </div>
                        </Label>
                        <Label
                          htmlFor="inline-method-etransfer"
                          className="flex items-center gap-3 border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
                        >
                          <RadioGroupItem value="etransfer" id="inline-method-etransfer" />
                          <Banknote className="h-5 w-5 text-muted-foreground shrink-0" />
                          <div>
                            <div className="font-medium text-sm">E-Transfer</div>
                            <p className="text-xs text-muted-foreground">Send payment via Interac e-Transfer</p>
                          </div>
                        </Label>
                      </RadioGroup>
                      {methodTotal}
                      <Button onClick={handlePaymentOnly} disabled={processingPayment} className="w-full" size="lg">
                        {processingPayment ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Continue to Payment
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}

                {/* ── Fully Paid ── */}
                {quote.status === "accepted" && quote.payment_status === "paid" && (
                  <div className="flex items-center gap-3 bg-primary/10 rounded-lg p-4">
                    <CheckCircle className="h-6 w-6 text-primary shrink-0" />
                    <div>
                      <div className="font-display font-semibold text-sm">Payment Received — Thank You!</div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Your payment of ${totalAmount.toFixed(2)} has been confirmed. Our team will be in touch with next steps.
                      </p>
                    </div>
                  </div>
                )}

                {/* ── Declined ── */}
                {quote.status === "declined" && (
                  <div className="flex items-center gap-3 bg-destructive/10 rounded-lg p-4">
                    <XCircle className="h-5 w-5 text-destructive shrink-0" />
                    <div>
                      <div className="font-medium text-sm">{docLabel} Declined</div>
                      <p className="text-xs text-muted-foreground">
                        If you change your mind, feel free to contact us anytime.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* ─── 5. Company Contact Footer ─── */}
        <div className="text-center space-y-2 py-6 font-secondary">
          <p className="text-sm text-muted-foreground">Questions about this {docLabel.toLowerCase()}?</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            {company.company_phone && (
              <a href={`tel:${company.company_phone}`} className="flex items-center gap-1.5 text-primary hover:underline">
                <Phone className="h-3.5 w-3.5" /> {company.company_phone}
              </a>
            )}
            {company.company_email && (
              <a href={`mailto:${company.company_email}`} className="flex items-center gap-1.5 text-primary hover:underline">
                <Mail className="h-3.5 w-3.5" /> {company.company_email}
              </a>
            )}
          </div>
          {company.company_address && (
            <p className="text-xs text-muted-foreground">{company.company_address}</p>
          )}
        </div>
      </main>

      {/* ─── Sticky Mobile CTA Bar ─── (lifts above the cookie banner while it shows, #1126) */}
      {showStickyBar && stickyCtaLabel && (
        <div className="fixed bottom-[var(--cookie-banner-height,0px)] left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t border-border p-3 no-print md:hidden">
          <Button onClick={scrollToAction} className="w-full" size="lg">
            <ArrowDown className="h-4 w-4 mr-2" />
            {stickyCtaLabel}
          </Button>
        </div>
      )}

      {/* ─── Payment Method Picker Dialog (for approve flow) ─── */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">How would you like to pay?</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <p className="text-sm text-muted-foreground">
              {showAmountChoice
                ? "Pay a deposit to confirm your booking, or pay the full amount now. Then choose how you'd like to pay."
                : "A deposit is required to confirm your booking. Please choose your preferred payment method."}
            </p>
            {amountChoice && (
              <PaymentAmountChoice
                value={paymentAmount}
                onChange={setPaymentAmount}
                options={amountChoice}
                idPrefix="dialog"
              />
            )}
            <RadioGroup
              value={paymentMethod}
              onValueChange={(v) => setPaymentMethod(v as "square" | "etransfer")}
              className="space-y-3"
            >
              <Label
                htmlFor="method-square"
                className="flex items-center gap-3 border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
              >
                <RadioGroupItem value="square" id="method-square" />
                <CreditCard className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="font-medium text-sm">Credit Card</div>
                  <p className="text-xs text-muted-foreground">Pay securely online via Square</p>
                </div>
              </Label>
              <Label
                htmlFor="method-etransfer"
                className="flex items-center gap-3 border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
              >
                <RadioGroupItem value="etransfer" id="method-etransfer" />
                <Banknote className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="font-medium text-sm">E-Transfer</div>
                  <p className="text-xs text-muted-foreground">Send payment via Interac e-Transfer</p>
                </div>
              </Label>
            </RadioGroup>
            {methodTotal}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} disabled={processingPayment}>
                Cancel
              </Button>
              <Button onClick={handlePaymentConfirm} disabled={processingPayment}>
                {processingPayment ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve & Continue
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
};

export default PublicQuotePage;
